import _ from 'lodash';
import apiFetch from '@scale/frontend-lib/api-fetch';

import {
  TaskStatsByProjectResponse,
  TaskStatsByProjectParams,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByProject';
import {
  TaskStatsByTypeResponse,
  TaskStatsByTypeParams,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByType';
import {
  TaskStatsByDayResponse,
  TaskStatsByWeekResponse,
  TaskStatsByDayParams,
  TaskStatsByWeekParams,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByDay';

import {
  TaskStatsByDayByBatchResponse,
  TaskStatsByDayByBatchParams,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByDayByBatch';

import {
  TaskStatsByBatchResponse,
  TaskStatsByBatchParams,
  TaskStats,
} from '@scale/shared/types/endpoints/customer/stats/tasks/taskStatsByBatch';
import { BatchStatsParams } from '@scale/shared/types/endpoints/customer/stats/batch';
import {
  MAASTasksChartParams,
  MAASTaskPerformanceChartData,
  MAASTaskQualityChartData,
  MAASTasksChartResponse,
} from '@scale/shared/types/endpoints/customer/stats/tasks/maasTasksChart';
import {
  AttributesQualityResponse,
  LabelErrorQualityResponse,
  PrecisionRecallQualityResponse,
  QualityParams,
  TypeErrorQualityResponse,
} from '@scale/shared/types/endpoints/customer/stats/qualityStats';
import {
  TurnAroundTimeRequest,
  TurnAroundTimeResponse,
  InterRaterAgreementScoreRequest,
  InterRaterAgreementScoreResponse,
} from '@scale/shared/types/endpoints/customer/stats/genAIProjectMetrics';

export async function getUserTasksByDay(
  filters: TaskStatsByDayParams,
): Promise<TaskStatsByDayResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-day`, { query });
}

export async function getUserTasksByWeek(
  filters: TaskStatsByWeekParams,
): Promise<TaskStatsByWeekResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-week`, { query });
}

export async function getTasksStatsByType(
  filters: TaskStatsByTypeParams,
): Promise<TaskStatsByTypeResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-type`, { query });
}

export async function getTasksStatsByProject(
  filters: TaskStatsByProjectParams,
): Promise<TaskStatsByProjectResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-project`, { query });
}

export async function getTasksStatsByBatch(
  filters: TaskStatsByBatchParams,
): Promise<TaskStatsByBatchResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-batch`, { query });
}

export async function getTasksStatsByDayByBatch(
  filters: TaskStatsByDayByBatchParams,
): Promise<TaskStatsByDayByBatchResponse> {
  const query = _.omitBy(filters, _.isNil);

  return apiFetch(null, `internal/stats/tasks/by-day-by-batch`, { query });
}

export async function getBatchStats({
  id,
  ...query
}: BatchStatsParams & { id: string }): Promise<TaskStats> {
  return apiFetch(null, `internal/stats/batches/${id}`, { query });
}

export async function getMAASTaskPerformanceStats(
  filters: MAASTasksChartParams,
): Promise<MAASTasksChartResponse<MAASTaskPerformanceChartData>> {
  const query = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/tasks/maas-task-performance', { query });
}

export async function getMAASTaskQualityStats(
  filters: MAASTasksChartParams,
): Promise<MAASTasksChartResponse<MAASTaskQualityChartData>> {
  const query = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/tasks/maas-task-quality', { query });
}

export async function getPrecisionRecallQuality(
  filters: QualityParams,
): Promise<PrecisionRecallQualityResponse> {
  const body = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/quality/precision-recall', { method: 'POST', body });
}

export async function getLabelQuality(filters: QualityParams): Promise<LabelErrorQualityResponse> {
  const body = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/quality/error-info', { method: 'POST', body });
}

export async function getErrorTypeQuality(
  filters: QualityParams,
): Promise<TypeErrorQualityResponse> {
  const body = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/quality/error-type', { method: 'POST', body });
}

export async function getAttributesQuality(
  filters: QualityParams,
): Promise<AttributesQualityResponse> {
  const body = _.omitBy(filters, _.isNil);
  return apiFetch(null, 'internal/stats/quality/attributes', { method: 'POST', body });
}

export async function getTurnAroundTime(
  params: TurnAroundTimeRequest,
): Promise<TurnAroundTimeResponse> {
  return apiFetch<TurnAroundTimeResponse>(null, 'internal/stats/genai-metrics/turn-around-time', {
    method: 'POST',
    body: params,
  });
}

export async function getInterRaterAgreementScore(
  params: InterRaterAgreementScoreRequest,
): Promise<InterRaterAgreementScoreResponse> {
  return apiFetch<InterRaterAgreementScoreResponse>(
    null,
    'internal/stats/genai-metrics/inter-rater-agreement',
    {
      method: 'POST',
      body: params,
    },
  );
}
